import { useState } from 'react'
import { AdvancedMarker, InfoWindow } from '@vis.gl/react-google-maps'
import { useDataLayerEntriesQuery } from '~/api'
import {
  aHoverDataLayerEntry,
  aVisibleLayers,
  useAtom,
  useAtomValue,
} from '~/atoms'
import {
  DATA_LAYER_ENTRY_FIELDS,
  type DataLayerEntry,
  getDataLayerEntryLabelValues,
} from '~/models'
import { fmtAddress } from '~/utils'

function GoogleDataLayerPopup({
  entry,
  onClose,
}: {
  entry: DataLayerEntry
  onClose: () => void
}) {
  return (
    <InfoWindow
      position={{
        lat: entry.property.latitude,
        lng: entry.property.longitude,
      }}
      maxWidth={400}
      onCloseClick={onClose}>
      <div className="p-1">
        <p className="mb-1 text-sm font-medium leading-tight">{entry.name}</p>
        <p className="mb-1 whitespace-pre-line text-sm">
          {fmtAddress(entry.property)}
        </p>
        <dl className="text-sm">
          {getDataLayerEntryLabelValues(entry, DATA_LAYER_ENTRY_FIELDS).map(
            ({ label, value, field }) => {
              let formattedValue = value

              if (field?.type === 'number' && field.maskType !== 'year') {
                formattedValue = formattedValue.toLocaleString()
              }
              if (field?.prefix) {
                formattedValue = `${field.prefix}${formattedValue}`
              }
              if (field?.suffix) {
                formattedValue = `${formattedValue}${field.suffix}`
              }

              return (
                <div key={label} className="mb-1 grid grid-cols-3 gap-2">
                  <dt className="col-span-1 font-medium">{label}:</dt>
                  <dd className="col-span-2">{formattedValue}</dd>
                </div>
              )
            }
          )}
        </dl>
      </div>
    </InfoWindow>
  )
}

function GoogleDataLayer({ dataLayerId }: { dataLayerId: string }) {
  const [visibleEntry, setVisibleEntry] = useState<DataLayerEntry>()
  const [hoverDataLayerEntry] = useAtom(aHoverDataLayerEntry)
  const { dataLayerEntries } = useDataLayerEntriesQuery({
    key: ['data-layer-entries', dataLayerId],
    find: {
      'dataLayer._id': dataLayerId,
    },
  })
  const popupEntry = visibleEntry ?? hoverDataLayerEntry

  return (
    <>
      {dataLayerEntries.map((entry) => (
        <AdvancedMarker
          key={entry._id}
          position={{
            lat: entry.property.latitude,
            lng: entry.property.longitude,
          }}
          onClick={() => {
            setVisibleEntry(entry)
          }}>
          <div className="h-4 w-4 rounded-full border-2 border-white bg-primary shadow-md" />
        </AdvancedMarker>
      ))}
      {popupEntry && (
        <GoogleDataLayerPopup
          key={popupEntry._id}
          entry={popupEntry}
          onClose={() => setVisibleEntry(undefined)}
        />
      )}
    </>
  )
}

export function GoogleDataLayers() {
  const visibleDataLayers = useAtomValue(aVisibleLayers)

  return (
    <>
      {visibleDataLayers.map((dataLayerId) => (
        <GoogleDataLayer key={dataLayerId} dataLayerId={dataLayerId} />
      ))}
    </>
  )
}
